import { InfoModal } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useEmulateUser } from '@backrs_ui/grow-ui/src/hooks/useEmulateUser';
import {
  Assignee,
  InstitutionMember,
} from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';
import { useFetchUserPermissionsQuery } from '@backrs_ui/grow-ui/src/redux/user/user.api';
import { UI_PERMISSIONS } from '@backrs_ui/grow-ui/src/redux/user/user.transformer';
import { personName } from '@backrs_ui/grow-ui/src/utils/personName';

import { Alert, Box, Button, Snackbar, type SxProps, type Theme, Typography } from '@mui/material';

const {
  modules: { coachingDashboard },
} = appStrings;

type MemberDetailActionButtonsProps = Readonly<{
  member: Assignee | InstitutionMember;
}>;

export const MemberDetailActionButtons = ({ member }: MemberDetailActionButtonsProps) => {
  const { data: userPermissions } = useFetchUserPermissionsQuery(undefined);
  const canEmulate = userPermissions?.[UI_PERMISSIONS.EMULATE_USERS]?.allowed ?? false;

  const {
    isRequestingToken,
    isDialogOpen,
    error: emulationError,
    handleEmulateClick,
    handleCloseDialog,
    handleConfirmEmulation,
    handleCloseError,
  } = useEmulateUser(member.id.toString());

  if (!canEmulate) return null;

  return (
    <>
      <Button
        id="member-detail-emulate-btn"
        hierarchy="Primary"
        size="md"
        onClick={handleEmulateClick}
      >
        {coachingDashboard.emulateLabel}
      </Button>
      <InfoModal
        open={isDialogOpen}
        onClose={handleCloseDialog}
        width="sm"
        featuredIcon={{
          iconName: 'Users01',
          size: 'lg',
          color: 'Colors/Foreground/fg-brand-primary',
        }}
        headerContent={<Typography variant="h6">Emulate User</Typography>}
        bodyContent={
          <Box sx={modalContentSx}>
            <Typography>{coachingDashboard.emulateConfirmation(personName(member))}</Typography>
            <Box sx={modalActionsSx}>
              <Button
                id="member-detail-emulate-cancel-btn"
                hierarchy="Secondary gray"
                size="md"
                onClick={handleCloseDialog}
              >
                Cancel
              </Button>
              <Button
                id="member-detail-emulate-confirm-btn"
                hierarchy="Primary"
                size="md"
                onClick={handleConfirmEmulation}
                disabled={isRequestingToken}
              >
                {isRequestingToken ? 'Starting...' : 'Start Emulation'}
              </Button>
            </Box>
          </Box>
        }
      />
      <Snackbar
        open={!!emulationError}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {emulationError}
        </Alert>
      </Snackbar>
    </>
  );
};

const modalContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const modalActionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-xl'],
  justifyContent: 'flex-end',
});
